<script lang="ts">
  /**
   * 穴埋め（card-schema.md §3.2、judge.md §4.1、components.md §1.3）。
   * 照合（client）→ 外れて client-then-server なら sandboxd で実行して判定する。
   */
  import { onDestroy } from 'svelte';
  import { fillTemplate, gradeBlanks } from '../../lib/judge';
  import type { FillBlankCard } from '../../lib/cards';
  import CardShell from '../CardShell.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import type { GradeResult } from '../types';
  import type { FillBlankAnswer } from './answer';
  import { localMismatch, sandboxResult, templateSegments } from './fill-blank';
  import { gradeOnSandbox } from './run';
  import { RunState } from './run-state.svelte';
  import RunStatus from './RunStatus.svelte';

  interface Props {
    card: FillBlankCard;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: FillBlankAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    ongrade?: ((result: GradeResult, answer: FillBlankAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
  }: Props = $props();

  const segments = $derived(templateSegments(card.template));
  const blankCount = $derived(card.blanks.length);
  const widthOf = (id: number) => card.blanks.find((b) => b.id === id)?.width ?? 6;
  const firstAccept = (id: number) => card.blanks.find((b) => b.id === id)?.accept[0] ?? '';

  let values = $state<Record<number, string>>({});
  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    values = savedAnswer ? { ...savedAnswer.values } : {};
  });

  /** 採点した回答（親が answer を返さなくても採点後の表示に使う） */
  let gradedAnswer = $state<FillBlankAnswer | undefined>();
  const shown = $derived(gradedAnswer ?? savedAnswer);

  function finish(r: GradeResult, a: FillBlankAnswer) {
    gradedAnswer = a;
    ongrade?.(r, a);
  }

  const run = new RunState();
  onDestroy(() => run.dispose());

  const graded = $derived(result !== undefined);
  const filled = $derived(card.blanks.every((b) => (values[b.id] ?? '').trim() !== ''));
  const gradeState = $derived(
    graded
      ? 'graded'
      : run.running
        ? 'grading'
        : Object.values(values).some((v) => v !== '')
          ? 'answering'
          : 'idle',
  );
  /** 採点後の空欄ごとの正誤。実行で AC になったら全部正解として扱う */
  const perBlank = $derived.by<Record<number, boolean> | null>(() => {
    if (!graded || !shown) return null;
    if (result?.verdict === 'AC') {
      return Object.fromEntries(card.blanks.map((b) => [b.id, true]));
    }
    return shown.perBlank;
  });

  async function submit() {
    if (graded || run.running || !filled) return;
    const snapshot = { ...values };
    const local = gradeBlanks(card.blanks, snapshot);
    const wrong = card.blanks.filter((b) => !local.perBlank[b.id]).map((b) => b.id);
    if (local.allCorrect) {
      finish(
        { verdict: 'AC', message: '空欄がすべて一致しました。' },
        { kind: 'fill-blank', values: snapshot, perBlank: local.perBlank, method: 'local' },
      );
      return;
    }
    if (card.grading !== 'client-then-server' || card.expected === undefined) {
      finish(localMismatch(wrong), {
        kind: 'fill-blank',
        values: snapshot,
        perBlank: local.perBlank,
        method: 'local',
      });
      return;
    }
    const expected = card.expected;
    const judge = await run.start((hooks) =>
      gradeOnSandbox(
        card,
        fillTemplate(card.template, snapshot),
        [{ in: card.stdin ?? '', out: expected }],
        hooks,
      ),
    );
    if (!judge) return;
    finish(sandboxResult(judge, expected), {
      kind: 'fill-blank',
      values: snapshot,
      perBlank: local.perBlank,
      method: 'sandbox',
      ...(judge.compileOutput !== '' ? { compileOutput: judge.compileOutput } : {}),
    });
  }

  function onInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.isComposing && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      void submit();
    }
  }
</script>

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  prompt="空欄に入るコードを書いてください。"
  {showUseCase}
  canSubmit={filled && !run.running}
  onsubmit={() => void submit()}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  <!-- prettier-ignore -->
  <pre class="code template" aria-label="穴埋めのコード" data-testid="template"><code>{#each segments as seg, i (i)}{#if seg.kind === 'text'}{seg.text}{:else}<span class="blank-wrap" class:ok={perBlank?.[seg.id] === true} class:ng={perBlank?.[seg.id] === false}><input
          type="text"
          class="blank-input"
          style:width="{widthOf(seg.id) + 2}ch"
          spellcheck="false"
          autocapitalize="off"
          autocomplete="off"
          aria-label="空欄 {seg.id} / {blankCount}"
          aria-invalid={perBlank?.[seg.id] === false ? 'true' : undefined}
          data-testid="blank-{seg.id}"
          readonly={graded || run.running}
          bind:value={values[seg.id]}
          onkeydown={onInputKeydown}
        />{#if perBlank?.[seg.id] === true}<span class="mark ok-mark" aria-hidden="true">✓</span>{:else if perBlank?.[seg.id] === false}<span class="mark ng-mark" aria-hidden="true">✕</span>{/if}</span>{/if}{/each}</code></pre>

  {#if card.stdin}
    <div class="stdin">
      <h2 class="sub">標準入力</h2>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="標準入力">{card.stdin.replace(/\n$/, '')}</pre>
    </div>
  {/if}

  <RunStatus {run} onretry={() => void submit()} />

  {#snippet feedback()}
    {#if perBlank && result?.verdict !== 'AC'}
      <ul class="blank-answers" data-testid="blank-answers">
        {#each card.blanks as b (b.id)}
          {#if perBlank[b.id] === false}
            <li>
              <span class="mark ng-mark" aria-hidden="true">✕</span>
              空欄 {b.id} の答えの例: <code>{firstAccept(b.id)}</code>
            </li>
          {/if}
        {/each}
      </ul>
    {/if}
    {#if result?.verdict === 'WA' && result.expected !== undefined && result.actual !== undefined}
      <OutputPanel mode="diff" expected={result.expected} actual={result.actual} />
    {:else if result?.verdict === 'RE' && result.stderr}
      <OutputPanel mode="plain" stderr={result.stderr} />
    {:else if result?.verdict === 'CE' && shown?.compileOutput}
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="コンパイラの出力">{shown.compileOutput}</pre>
    {/if}
  {/snippet}
</CardShell>

<style>
  .template {
    padding: var(--space-4);
    white-space: pre-wrap;
  }
  .blank-wrap {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    vertical-align: baseline;
  }
  .blank-input {
    display: inline-block;
    min-height: 0;
    min-width: 4ch;
    max-width: 100%;
    padding: 0 var(--space-1);
    font-family: var(--font-mono);
    font-size: var(--fs-code);
    line-height: var(--lh-code);
    border: 0;
    border-bottom: 2px solid var(--border-strong);
    border-radius: var(--radius-xs) var(--radius-xs) 0 0;
    background: var(--surface);
    color: var(--text);
  }
  .blank-input:focus-visible {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: 1px;
  }
  .blank-wrap.ok .blank-input {
    border-bottom-color: var(--ok-text);
  }
  .blank-wrap.ng .blank-input {
    border: 2px solid var(--err-text);
  }
  .mark {
    font-weight: var(--fw-bold);
  }
  .ok-mark {
    color: var(--ok-text);
  }
  .ng-mark {
    color: var(--err-text);
  }
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .blank-answers {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
</style>
